// NewCustomer.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

const Section = ({ title, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-3 border">
      <div
        className="fw-bold p-2 border-bottom d-flex justify-content-between align-items-center"
        style={{ cursor: "pointer", backgroundColor: "#DFE4EB" }}
        onClick={() => setOpen(!open)}
      >
        <span>{title}</span>
        <span>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div className="row g-3 p-3">{children}</div>}
    </div>
  );
};

const INDUSTRIES = ["IT", "Construction", "Manufacturing", "Consulting", "Marketing", "Healthcare", "Finance", "Education", "Other"];
const CUSTOMER_TYPES = ["Individual", "Company", "Government", "NGO", "Other"];
const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AED", "SGD"];
const PAYMENT_TERMS = ["Net 15", "Net 30", "Net 45", "Due on receipt"];
const STATUSES = ["Active", "Inactive", "Blacklisted"];
const ADDRESS_TYPES = ["Billing", "Shipping", "Home", "Work", "Other"];

const emptyAddress = () => ({ addressType: "Billing", addressLine1: "", addressLine2: "", city: "", state: "", country: "", postalCode: "" });

const initialForm = {
  customerName: "", customerCode: "", customerType: "Company",
  industry: "", website: "",
  addresses: [emptyAddress()],
  accountManager: "", status: "Active", currency: "INR",
  paymentTerms: "Net 30", taxNumber: "", notes: "", tags: [],
};

export default function NewCustomer() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [tagInput, setTagInput] = useState("");
  const [activeTab, setActiveTab] = useState("basic");
  const [navbarColor, setNavbarColor] = useState("#080235");
  const refs = useRef({});
  const setRef = (name) => (el) => { refs.current[name] = el; };
  const token = localStorage.getItem("token");

  // Bootstrap loader
  useEffect(() => {
    const bootstrapLink = document.createElement("link");
    bootstrapLink.rel = "stylesheet";
    bootstrapLink.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
    document.head.appendChild(bootstrapLink);
    const iconsLink = document.createElement("link");
    iconsLink.rel = "stylesheet";
    iconsLink.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
    document.head.appendChild(iconsLink);
    return () => { bootstrapLink.remove(); iconsLink.remove(); };
  }, []);

  // Auth check
  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
    if (!roles.includes("ROLE_ADMIN") && !roles.includes("ROLE_CUSTOMER_MANAGER")) {
      toast.error("Unauthorized."); navigate("/"); return;
    }
  }, [navigate, token]);

  // Navbar color
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (!token || !userId) return;
    fetch(`${API_BASE_URL}/api/appearance/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => { if (d?.navbarColor) setNavbarColor(d.navbarColor); })
      .catch(() => { });
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setTouched((t) => ({ ...t, [name]: true }));
  };

  const handleAddressChange = (idx, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.addresses];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...prev, addresses: updated };
    });
  };
  const addAddress = () => setFormData((prev) => ({ ...prev, addresses: [...prev.addresses, emptyAddress()] }));
  const removeAddress = (idx) => setFormData((prev) => ({ ...prev, addresses: prev.addresses.filter((_, i) => i !== idx) }));

  const addTag = (val) => {
    const t = val.trim();
    if (t && !formData.tags.includes(t)) setFormData((prev) => ({ ...prev, tags: [...prev.tags, t] }));
    setTagInput("");
  };
  const removeTag = (tag) => setFormData((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tag) }));

  const inputClass = (field) => {
    if (errors[field]) return "form-control is-invalid";
    if (touched[field] && !errors[field]) return "form-control is-valid";
    return "form-control";
  };
  const selectClass = (field) => {
    if (errors[field]) return "form-select is-invalid";
    if (touched[field] && !errors[field]) return "form-select is-valid";
    return "form-select";
  };

  const validate = () => {
    const e = {};
    if (!formData.customerName.trim()) e.customerName = "Customer name is required.";
    if (!formData.customerType) e.customerType = "Customer type is required.";
    if (!formData.status) e.status = "Status is required.";
    if (!formData.currency) e.currency = "Currency is required.";
    if (formData.website && !/^https?:\/\/.+/.test(formData.website))
      e.website = "Website must start with http:// or https://";
    return e;
  };

  const focusFirstError = (errs) => {
    const firstKey = Object.keys(errs)[0];
    const el = refs.current[firstKey];
    if (el?.focus) { el.focus(); el.scrollIntoView({ behavior: "smooth", block: "center" }); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    setErrors(newErrors);
    const touchedAll = {};
    Object.keys(initialForm).forEach((k) => (touchedAll[k] = true));
    setTouched(touchedAll);
    if (Object.keys(newErrors).length > 0) {
      focusFirstError(newErrors);
      toast.error("Please fix form errors before submitting.");
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/customers/add`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success("Customer created successfully!", { onClose: () => navigate("/allcustomers") });
      setFormData(initialForm);
      setErrors({});
      setTouched({});
    } catch (err) {
      toast.error(err.message || "Failed to create customer.");
    }
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />
      <div className="fixed-top">
        <AdminNavbar />
      </div>
      <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>
        <div className="mb-3">
          <h4 className="m-0">
            <i className="bi bi-person-plus me-2"></i> New Customer
          </h4>
          <div className="d-flex justify-content-between align-items-center mt-2">
            <div>
              <button type="submit" form="customerForm" className="btn btn-primary me-2">Save</button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>Cancel</button>
            </div>
            <a href="/allcustomers" className="text-primary text-decoration-none me-3">List</a>
          </div>
        </div>

        <form id="customerForm" onSubmit={handleSubmit}>

          {/* Basic Information */}
          <Section title="Basic Information">
            <div className="col-md-3">
              <label className="form-label">Customer Code</label>
              <input type="text" className="form-control" value="Auto-generated" readOnly
                style={{ background: "#f0f0ee", fontFamily: "monospace", color: "#888" }} />
              <div className="form-text">Format: CUST-001 — auto-assigned on creation</div>
            </div>

            <div className="col-md-3">
              <label className="form-label">Customer Name <span className="text-danger">*</span></label>
              <input
                ref={setRef("customerName")} type="text" name="customerName"
                className={inputClass("customerName")} value={formData.customerName}
                onChange={handleChange} placeholder="e.g. Acme Corporation"
              />
              {errors.customerName && <div className="invalid-feedback">{errors.customerName}</div>}
            </div>

            <div className="col-md-3">
              <label className="form-label">Customer Type <span className="text-danger">*</span></label>
              <select ref={setRef("customerType")} name="customerType" className={selectClass("customerType")} value={formData.customerType} onChange={handleChange}>
                {CUSTOMER_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
              {errors.customerType && <div className="invalid-feedback">{errors.customerType}</div>}
            </div>

            <div className="col-md-3">
              <label className="form-label">Industry</label>
              <select name="industry" className="form-select" value={formData.industry} onChange={handleChange}>
                <option value="">Select industry…</option>
                {INDUSTRIES.map((i) => <option key={i}>{i}</option>)}
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label">Website</label>
              <input
                ref={setRef("website")} type="url" name="website"
                className={inputClass("website")} value={formData.website}
                onChange={handleChange} placeholder="https://example.com"
              />
              {errors.website && <div className="invalid-feedback">{errors.website}</div>}
            </div>

            <div className="col-md-3">
              <label className="form-label">Account Manager</label>
              <input type="text" name="accountManager" className="form-control" value={formData.accountManager} onChange={handleChange} placeholder="Manager name or ID" />
            </div>

            <div className="col-md-3">
              <label className="form-label">Status <span className="text-danger">*</span></label>
              <select ref={setRef("status")} name="status" className={selectClass("status")} value={formData.status} onChange={handleChange}>
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
              {errors.status && <div className="invalid-feedback">{errors.status}</div>}
            </div>
          </Section>

          {/* Tabs */}
          <ul className="nav nav-tabs mb-2" style={{ backgroundColor: navbarColor }}>
            {[
              ["basic", "Address"],
              ["billing", "Financial"],
              ["notes", "Notes & Tags"],
            ].map(([tabId, label]) => (
              <li className="nav-item" key={tabId}>
                <button
                  type="button"
                  className={`nav-link ${activeTab === tabId ? "active" : ""}`}
                  onClick={() => setActiveTab(tabId)}
                  style={{
                    color: activeTab === tabId ? navbarColor : "#fff",
                    backgroundColor: activeTab === tabId ? "#fff" : "transparent",
                    fontWeight: activeTab === tabId ? "600" : "normal",
                  }}
                >
                  {label}
                </button>
              </li>
            ))}
          </ul>

          <div className="border p-3 mb-3">
            {activeTab === "basic" && (
              <>
                <Section title="Address Details">
                  {formData.addresses.map((addr, idx) => (
                    <div key={idx} className="col-12 border rounded p-0 mb-2 overflow-hidden">
                      {/* Card header */}
                      <div className="d-flex justify-content-between align-items-center px-3 py-2" style={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #dee2e6" }}>
                        <div className="d-flex align-items-center gap-2">
                          <span className="badge bg-secondary">
                            {addr.addressType || "Billing"}
                          </span>
                          <span className="text-muted small">Address {idx + 1}</span>
                        </div>
                        {formData.addresses.length > 1 && (
                          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeAddress(idx)}>Remove</button>
                        )}
                      </div>

                      {/* Fields */}
                      <div className="row g-2 p-3">
                        <div className="col-md-3">
                          <label className="form-label">Address Type</label>
                          <select className="form-select" value={addr.addressType} onChange={(e) => handleAddressChange(idx, "addressType", e.target.value)}>
                            {ADDRESS_TYPES.map((t) => <option key={t}>{t}</option>)}
                          </select>
                        </div>
                        <div className="col-md-9">
                          <label className="form-label">Address Line 1</label>
                          <input type="text" className="form-control" value={addr.addressLine1} onChange={(e) => handleAddressChange(idx, "addressLine1", e.target.value)} placeholder="Street address" />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Address Line 2</label>
                          <input type="text" className="form-control" value={addr.addressLine2} onChange={(e) => handleAddressChange(idx, "addressLine2", e.target.value)} placeholder="Apartment, suite, building (optional)" />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">City</label>
                          <input type="text" className="form-control" value={addr.city} onChange={(e) => handleAddressChange(idx, "city", e.target.value)} placeholder="City" />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">State / Province</label>
                          <input type="text" className="form-control" value={addr.state} onChange={(e) => handleAddressChange(idx, "state", e.target.value)} placeholder="State" />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Country</label>
                          <input type="text" className="form-control" value={addr.country} onChange={(e) => handleAddressChange(idx, "country", e.target.value)} placeholder="Country" />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Postal Code</label>
                          <input type="text" className="form-control" value={addr.postalCode} onChange={(e) => handleAddressChange(idx, "postalCode", e.target.value)} placeholder="PIN / ZIP" />
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="col-12">
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={addAddress}>+ Add Address</button>
                  </div>
                </Section>

              </>
            )}

            {activeTab === "billing" && (
              <Section title="Financial">

                <div className="col-md-3">
                  <label className="form-label">Currency <span className="text-danger">*</span></label>
                  <select ref={setRef("currency")} name="currency" className={selectClass("currency")} value={formData.currency} onChange={handleChange}>
                    {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                  {errors.currency && <div className="invalid-feedback">{errors.currency}</div>}
                </div>
                <div className="col-md-3">
                  <label className="form-label">Payment Terms</label>
                  <select name="paymentTerms" className="form-select" value={formData.paymentTerms} onChange={handleChange}>
                    {PAYMENT_TERMS.map((p) => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Tax Number / GST</label>
                  <input type="text" name="taxNumber" className="form-control" value={formData.taxNumber} onChange={handleChange} placeholder="GST/Tax registration" />
                </div>
              </Section>
            )}

            {activeTab === "notes" && (
              <>
                <Section title="Tags">
                  <div className="col-md-12">
                    <label className="form-label">Tags</label>
                    <div className="border rounded p-2 d-flex flex-wrap gap-2 align-items-center" style={{ minHeight: 42, background: "#fafaf9" }}>
                      {formData.tags.map((t) => (
                        <span key={t} className="badge" style={{ background: "#eaf0f8", color: "#3a6090", border: "1px solid #d0dff0", fontSize: 12 }}>
                          {t}
                          <button type="button" className="btn-close ms-1" style={{ fontSize: 8 }} onClick={() => removeTag(t)} />
                        </span>
                      ))}
                      <input
                        type="text"
                        className="border-0 flex-grow-1"
                        style={{ outline: "none", minWidth: 100, background: "transparent", fontSize: 13 }}
                        placeholder={formData.tags.length ? "" : "Add tag, press Enter…"}
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(tagInput); }
                          if (e.key === "Backspace" && !tagInput && formData.tags.length) {
                            setFormData((p) => ({ ...p, tags: p.tags.slice(0, -1) }));
                          }
                        }}
                        onBlur={() => tagInput.trim() && addTag(tagInput)}
                      />
                    </div>
                    <div className="form-text">Press Enter or comma to add tags</div>
                  </div>
                </Section>

                <Section title="Internal Notes">
                  <div className="col-md-12">
                    <label className="form-label">Notes</label>
                    <textarea
                      name="notes" className="form-control" rows={4}
                      value={formData.notes} onChange={handleChange}
                      placeholder="Internal notes about this customer…"
                    />
                  </div>
                </Section>
              </>
            )}
          </div>

        </form>

        <div className="d-flex align-items-center mt-2 mb-4">
          <button type="submit" form="customerForm" className="btn btn-primary me-2">Save</button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>Cancel</button>
        </div>
      </div>
    </>
  );
}